/**
 * 법적 / 안내 정적 페이지 모음.
 * 본문은 실제 저장 항목과 운영 구조에 맞춘 *초안* — 유료 결제 시작 전 법무 검토 필요.
 * 사업자·책임자 정보는 lib/site-info.ts 에서 채운다.
 */
import { Link } from "react-router-dom";
import { PRIVACY_OFFICER, SUPPORT_EMAIL } from "@/lib/site-info";

const LAST_UPDATED = "2026-10-12";

const SECTION_GAP = "mt-10";
const HEADING = "text-xl font-semibold tracking-tight";
const PARAGRAPH = "mt-3 text-sm text-muted-foreground leading-relaxed";
const LIST = "mt-3 list-disc space-y-1.5 pl-5 text-sm text-muted-foreground leading-relaxed";

function LegalShell({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <main className="container max-w-2xl py-16">
      <p className="text-xs text-muted-foreground">Devit</p>
      <h1 className="mt-1 text-3xl font-bold tracking-tight">{title}</h1>
      <p className="mt-2 text-sm text-muted-foreground">최종 업데이트: {LAST_UPDATED}</p>
      <div>{children}</div>
      <p className="mt-12 text-xs text-muted-foreground">
        궁금한 점은{" "}
        <Link to="/contact" className="underline underline-offset-4">
          문의 페이지
        </Link>
        로 연락해 주세요.
      </p>
    </main>
  );
}

function MailLink({ email }: { email: string }): JSX.Element {
  return (
    <a href={`mailto:${email}`} className="underline underline-offset-4 hover:text-foreground">
      {email}
    </a>
  );
}

/** 처리 위탁 · 국외 이전 업체. 실제 계약/리전과 다르면 함께 고친다. */
const PROCESSORS: { name: string; purpose: string; location: string }[] = [
  {
    name: "DigitalOcean, LLC",
    purpose: "서버와 데이터베이스 운영",
    location: "싱가포르 데이터센터",
  },
  {
    name: "Resend, Inc.",
    purpose: "인증·비밀번호 재설정·초대 메일 발송",
    location: "미국",
  },
  {
    name: "Cloudflare, Inc.",
    purpose: "웹사이트 전송, 설치 파일 배포",
    location: "미국 및 전 세계 전송망",
  },
];

export function PrivacyPage(): JSX.Element {
  return (
    <LegalShell title="개인정보 처리방침">
      <p className={PARAGRAPH}>
        Devit(이하 "서비스")은 이용자의 개인정보를 소중히 다루며, 개인정보 보호법 등 관련 법령을
        지킵니다. 이 방침은 어떤 정보를 왜 수집하고 어떻게 보관하는지 설명합니다.
      </p>

      <section className={SECTION_GAP}>
        <h2 className={HEADING}>1. 수집하는 정보</h2>
        <ul className={LIST}>
          <li>가입 시 (필수): 이메일 주소, 비밀번호 (복원할 수 없는 해시로만 저장)</li>
          <li>가입 시 (선택): 이름</li>
          <li>
            서비스 이용 중: 조직·프로젝트 이름, GitHub 저장소 주소, 사용자가 직접 입력한 태스크
            제목과 설명, 예약 작업 내용, PR 제목과 주소
          </li>
          <li>
            자동 수집: 접속 IP와 접속 일시, AI 사용량 기록 (실행 횟수, 소요 시간, 토큰 수, 사용한 AI
            서비스와 모델 이름)
          </li>
          <li>웹 브라우저: 로그인 유지를 위한 인증 토큰과 화면 설정 (브라우저 저장소)</li>
        </ul>
      </section>

      <section className={SECTION_GAP}>
        <h2 className={HEADING}>2. 수집하지 않는 정보</h2>
        <p className={PARAGRAPH}>
          서비스는 이용자의 소스 코드, AI 서비스 API 키, GitHub 토큰, 첨부 파일 내용을 서버에
          저장하지 않습니다. AI 실행과 git 작업은 이용자의 컴퓨터에서 이루어지며, 키와 토큰은
          운영체제의 보안 저장소(macOS 키체인, Windows 자격 증명 관리자)에만 보관됩니다.
        </p>
        <p className={PARAGRAPH}>
          AI 실행 시 코드는 이용자의 컴퓨터에서 이용자가 선택한 AI 서비스(예: Anthropic, OpenAI,
          Google)로 직접 전달됩니다. 이 과정은 해당 AI 서비스의 약관과 개인정보 처리방침을 따르며,
          Devit 서버를 거치지 않습니다.
        </p>
      </section>

      <section className={SECTION_GAP}>
        <h2 className={HEADING}>3. 이용 목적</h2>
        <ul className={LIST}>
          <li>회원 식별, 로그인과 세션 관리</li>
          <li>조직·프로젝트·태스크 정보를 팀원 사이에 동기화</li>
          <li>사용량 통계 제공, 유료 플랜의 요금 계산</li>
          <li>인증·안내 메일 발송, 문의 응대, 부정 이용과 보안 사고 대응</li>
        </ul>
      </section>

      <section className={SECTION_GAP}>
        <h2 className={HEADING}>4. 보유 기간과 파기</h2>
        <ul className={LIST}>
          <li>회원 정보: 탈퇴할 때까지. 탈퇴하면 지체 없이 파기합니다.</li>
          <li>
            다른 멤버와 함께 쓰는 조직의 태스크·PR 기록은 조직의 자료로 남을 수 있으며, 이 경우
            탈퇴한 이용자를 알아볼 수 없도록 처리합니다.
          </li>
          <li>접속 기록: 3개월 (통신비밀보호법)</li>
          <li>
            유료 결제를 시작한 뒤의 계약·결제 기록: 5년, 소비자 불만·분쟁 처리 기록: 3년
            (전자상거래법)
          </li>
        </ul>
        <p className={PARAGRAPH}>
          전자 파일은 복구할 수 없는 방법으로 삭제합니다. 현재 탈퇴는{" "}
          <MailLink email={SUPPORT_EMAIL} /> 로 요청하실 수 있습니다.
        </p>
      </section>

      <section className={SECTION_GAP}>
        <h2 className={HEADING}>5. 처리 위탁과 국외 이전</h2>
        <p className={PARAGRAPH}>
          서비스 운영을 위해 아래 업체에 개인정보 처리를 맡기며, 이 과정에서 정보가 국외에
          보관·전송됩니다. 이전되는 항목은 1번의 정보 중 각 업무에 필요한 것으로 한정되고, 서비스
          이용 기간 동안 네트워크를 통해 수시로 전송됩니다. 국외 이전을 원하지 않으시면 탈퇴할 수
          있으나, 이 경우 서비스를 이용할 수 없습니다.
        </p>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border text-xs text-muted-foreground">
              <tr>
                <th className="py-2 pr-4 font-medium">업체</th>
                <th className="py-2 pr-4 font-medium">맡기는 업무</th>
                <th className="py-2 font-medium">위치</th>
              </tr>
            </thead>
            <tbody className="text-muted-foreground">
              {PROCESSORS.map((p) => (
                <tr key={p.name} className="border-b border-border/60 align-top">
                  <td className="py-2 pr-4 text-foreground">{p.name}</td>
                  <td className="py-2 pr-4">{p.purpose}</td>
                  <td className="py-2">{p.location}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className={PARAGRAPH}>
          법령에 따른 요청이 있는 경우를 제외하고, 개인정보를 제3자에게 제공하지 않습니다.
        </p>
      </section>

      <section className={SECTION_GAP}>
        <h2 className={HEADING}>6. 이용자의 권리</h2>
        <p className={PARAGRAPH}>
          언제든 본인 정보의 열람·정정·삭제·처리 정지를 요구할 수 있습니다. 비밀번호는 계정
          페이지에서 직접 바꿀 수 있고, 그 밖의 요청은 아래 책임자에게 메일로 보내 주시면 지체 없이
          처리합니다.
        </p>
        <p className={PARAGRAPH}>서비스는 만 14세 미만 아동의 가입을 받지 않습니다.</p>
      </section>

      <section className={SECTION_GAP}>
        <h2 className={HEADING}>7. 안전성 확보 조치</h2>
        <ul className={LIST}>
          <li>비밀번호 해시 저장, 모든 통신 구간 암호화(HTTPS)</li>
          <li>로그인 시도 횟수 제한, 인증 토큰의 짧은 유효 기간과 폐기 관리</li>
          <li>관리자 권한 최소화와 접근 기록 관리</li>
        </ul>
      </section>

      <section className={SECTION_GAP}>
        <h2 className={HEADING}>8. 개인정보 보호책임자</h2>
        <ul className={LIST}>
          {PRIVACY_OFFICER.name !== "" && <li>책임자: {PRIVACY_OFFICER.name}</li>}
          <li>
            연락처: <MailLink email={PRIVACY_OFFICER.email} />
          </li>
        </ul>
        <p className={PARAGRAPH}>
          개인정보 침해에 대한 상담은 개인정보침해신고센터(국번 없이 118, privacy.kisa.or.kr)나
          개인정보분쟁조정위원회(1833-6972, kopico.go.kr)에도 문의할 수 있습니다.
        </p>
      </section>

      <section className={SECTION_GAP}>
        <h2 className={HEADING}>9. 방침의 변경</h2>
        <p className={PARAGRAPH}>
          이 방침이 바뀌면 시행 7일 전에 이 페이지에 알리고, 이용자에게 불리한 변경은 30일 전에
          메일로도 알려 드립니다.
        </p>
      </section>
    </LegalShell>
  );
}

export function TermsPage(): JSX.Element {
  return (
    <LegalShell title="이용약관">
      <section className={SECTION_GAP}>
        <h2 className={HEADING}>1. 목적</h2>
        <p className={PARAGRAPH}>
          이 약관은 Devit(이하 "서비스")이 제공하는 태스크 관리와 로컬 AI 개발 자동화 서비스의 이용
          조건, 서비스와 이용자의 권리·의무를 정합니다.
        </p>
      </section>

      <section className={SECTION_GAP}>
        <h2 className={HEADING}>2. 가입과 계정</h2>
        <ul className={LIST}>
          <li>만 14세 이상이면 이 약관과 개인정보 처리방침에 동의하고 가입할 수 있습니다.</li>
          <li>
            이용자는 정확한 정보로 가입하고, 자신의 계정과 비밀번호를 안전하게 관리해야 합니다.
          </li>
          <li>계정이 무단으로 사용된 사실을 알게 되면 바로 서비스에 알려 주세요.</li>
        </ul>
      </section>

      <section className={SECTION_GAP}>
        <h2 className={HEADING}>3. 서비스의 내용</h2>
        <p className={PARAGRAPH}>
          서비스는 클라우드에서 조직·프로젝트·태스크 정보를 관리하고, 이용자 컴퓨터의 데스크톱 앱이
          이용자가 연결한 AI 서비스와 GitHub로 개발 작업을 수행하도록 돕습니다. AI 서비스와 GitHub
          이용에는 각 서비스의 계정·약관·요금이 별도로 적용되며, 그 비용은 이용자가 부담합니다.
        </p>
      </section>

      <section className={SECTION_GAP}>
        <h2 className={HEADING}>4. 유료 서비스</h2>
        <p className={PARAGRAPH}>
          현재 모든 플랜은 무료입니다. 유료 플랜을 도입할 때는 가격과 결제 조건을 가격 페이지에 미리
          알리며, 이용자가 직접 결제하기 전에는 요금이 청구되지 않습니다. 결제 취소와 환불은{" "}
          <Link to="/refund" className="underline underline-offset-4">
            환불 정책
          </Link>
          을 따릅니다.
        </p>
      </section>

      <section className={SECTION_GAP}>
        <h2 className={HEADING}>5. 이용자 콘텐츠</h2>
        <p className={PARAGRAPH}>
          이용자가 작성한 태스크·프로젝트 정보와 AI로 만든 결과물의 권리는 이용자에게 있습니다.
          서비스는 서비스를 제공하는 데 필요한 범위에서만 이를 처리합니다.
        </p>
      </section>

      <section className={SECTION_GAP}>
        <h2 className={HEADING}>6. 금지 행위</h2>
        <ul className={LIST}>
          <li>다른 사람의 계정이나 자격 증명을 도용하거나 공유하는 행위</li>
          <li>서비스의 정상 운영을 방해하는 과도한 자동 요청이나 공격 행위</li>
          <li>법령이나 다른 사람의 권리를 침해하는 콘텐츠를 만들거나 올리는 행위</li>
        </ul>
        <p className={PARAGRAPH}>
          금지 행위가 확인되면 사전 안내 후 이용을 제한할 수 있으며, 긴급한 보안 위험이 있으면 먼저
          제한한 뒤 알려 드립니다.
        </p>
      </section>

      <section className={SECTION_GAP}>
        <h2 className={HEADING}>7. 책임의 한계</h2>
        <p className={PARAGRAPH}>
          AI가 만든 코드는 틀리거나 다른 라이선스와 충돌할 수 있으므로, 이용자가 검토한 뒤 사용해야
          합니다. 서비스는 고의나 중대한 과실이 없는 한, AI 결과물의 사용이나 외부 서비스(AI 서비스,
          GitHub 등)의 장애로 생긴 손해에 대해 책임지지 않습니다.
        </p>
      </section>

      <section className={SECTION_GAP}>
        <h2 className={HEADING}>8. 탈퇴</h2>
        <p className={PARAGRAPH}>
          이용자는 언제든 탈퇴를 요청할 수 있습니다. 탈퇴 후 정보 처리는 개인정보 처리방침을
          따릅니다.
        </p>
      </section>

      <section className={SECTION_GAP}>
        <h2 className={HEADING}>9. 약관의 변경</h2>
        <p className={PARAGRAPH}>
          약관을 바꿀 때는 시행 7일 전(이용자에게 불리한 변경은 30일 전)에 이 페이지와 메일로
          알립니다. 변경에 동의하지 않으면 탈퇴할 수 있습니다.
        </p>
      </section>
    </LegalShell>
  );
}

export function RefundPage(): JSX.Element {
  return (
    <LegalShell title="환불 정책">
      <section className={SECTION_GAP}>
        <h2 className={HEADING}>현재 결제 상태</h2>
        <p className={PARAGRAPH}>
          지금은 모든 플랜이 무료이며 어떤 요금도 청구하지 않습니다. 유료 플랜을 도입하면 아래
          원칙에 따라 세부 기준을 이 페이지에 먼저 알려 드립니다.
        </p>
      </section>

      <section className={SECTION_GAP}>
        <h2 className={HEADING}>유료 플랜의 기본 원칙</h2>
        <ul className={LIST}>
          <li>
            결제 후 7일 이내에 유료 기능을 사용하지 않았다면 전자상거래법에 따라 전액 환불을 요청할
            수 있습니다.
          </li>
          <li>
            구독은 언제든 해지할 수 있으며, 해지하면 다음 결제일부터 요금이 청구되지 않습니다.
          </li>
          <li>이미 결제한 기간은 해지 후에도 끝날 때까지 그대로 이용할 수 있습니다.</li>
          <li>서비스 장애 등 서비스 책임으로 이용하지 못한 기간은 별도로 보상합니다.</li>
        </ul>
      </section>

      <section className={SECTION_GAP}>
        <h2 className={HEADING}>요청 방법</h2>
        <p className={PARAGRAPH}>
          <MailLink email={SUPPORT_EMAIL} /> 로 가입 이메일과 결제 일시를 보내 주세요. 확인 후
          결제한 수단으로 환불하며, 카드사 사정에 따라 반영까지 며칠 걸릴 수 있습니다.
        </p>
      </section>
    </LegalShell>
  );
}

export function ContactPage(): JSX.Element {
  return (
    <LegalShell title="문의">
      <section className={SECTION_GAP}>
        <p className={PARAGRAPH}>
          서비스 이용, 팀 도입, 개인정보, 결제 관련 문의는 아래 메일로 보내 주세요.
        </p>
        <ul className="mt-4 space-y-2 text-sm">
          <li>
            <strong>이메일</strong>: <MailLink email={SUPPORT_EMAIL} />
          </li>
          <li>
            <strong>답변 시간</strong>: 평일 10:00 – 18:00 (한국 시간)
          </li>
        </ul>
      </section>
    </LegalShell>
  );
}

export function NotFoundPage(): JSX.Element {
  return (
    <main className="container flex min-h-[60vh] flex-col items-center justify-center py-16 text-center">
      <p className="font-mono text-6xl font-bold tracking-tight text-muted-foreground">404</p>
      <h1 className="mt-4 text-2xl font-bold tracking-tight">페이지를 찾을 수 없습니다</h1>
      <p className="mt-2 max-w-sm text-sm text-muted-foreground">
        주소를 다시 확인해 주시거나, 아래 버튼으로 홈으로 돌아가세요.
      </p>
      <Link
        to="/"
        className="mt-6 inline-flex h-10 items-center justify-center rounded-md bg-primary px-5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
      >
        홈으로
      </Link>
    </main>
  );
}
